import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid } from '@mui/material';
import { BarStack, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import PropTypes from 'prop-types';

import {
  getParams,
  getMonth,
  thousandFormat,
  thousandFormat2,
  getMonths2,
  getMonths,
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  negativeActiveColor,
  negativeDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

// accessors
const x = (d) => d.month;
const y = (d) => d.year;

let tooltipTimeout;

const TopChart = ({
  queryData,
  period,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  defaultStartMonth,
  defaultMonth,
  handleFilter,
}) => {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const svgRef = useRef(null);
  const dragStartRef = useRef(null);
  const draggedRef = useRef(false);
  const [dragRect, setDragRect] = useState(null);
  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [colors, setColors] = useState([]);
  const [maxLine, setMaxLine] = useState(0);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    prepareData();
  }, [queryData, period]);

  const prepareData = useCallback(() => {
    let monthData = {};
    let monthTotals = {};
    let newData = [];
    let newTotals = [];
    let yearData = {};
    let yearTotals = {};
    let max_line = 0;

    let newColors = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    if (period !== 'month') selectedYears = selectedYears.sort((a, b) => a - b);

    let fiscalMonths = getMonths(defaultStartMonth);
    let fiscalMonths2 = getMonths2(defaultMonth, defaultStartMonth);

    fiscalMonths.forEach((month) => {
      monthData[month] = {};
      monthData[month]['month'] = month;
      monthData[month]['year'] = 0;
      monthData[month]['value'] = {};
      monthTotals[month] = {};
    });

    selectedYears.forEach((year) => {
      yearTotals[year] = {};
      yearData[year] = {};
      yearData[year]['value'] = {};
    });

    if (queryData['Top']) {
      queryData['Top'].forEach((item) => {
        let month = getMonth(item.Date);
        if (monthData[month] !== undefined) {
          if (fiscalMonths2.indexOf(month) > -1) {
            let item_key = item.FY + '_' + item.ExistingOrNew;
            if (!monthData[month][item_key]) {
              monthData[month][item_key] = parseFloat(
                item.WorkGeneratedAmount,
              );
              monthData[month]['value'][item_key] = item;
              monthData[month]['year'] = item.FY;
              monthData[month]['value'][item_key]['type'] = item.ExistingOrNew;
            }

            if (!monthTotals[month][item.ExistingOrNew])
              monthTotals[month][item.ExistingOrNew] = 0;

            monthTotals[month][item.ExistingOrNew] += parseFloat(
              item.WorkGeneratedAmount,
            );

            if (yearData[item.FY] && !yearData[item.FY][item.ExistingOrNew]) {
              yearData[item.FY][item.ExistingOrNew] = 0;
              yearData[item.FY]['year'] = item.FY;

              yearData[item.FY]['value'][item.ExistingOrNew] = {};
              yearData[item.FY]['value'][item.ExistingOrNew][
                'WorkGeneratedAmount'
              ] = 0;
              yearData[item.FY]['value'][item.ExistingOrNew]['type'] =
                item.ExistingOrNew;
              yearData[item.FY]['value'][item.ExistingOrNew]['year'] = item.FY;
            }

            if (yearData[item.FY])
              yearData[item.FY][item.ExistingOrNew] += parseFloat(
                item.WorkGeneratedAmount,
              );

            if (yearTotals[item.FY] && !yearTotals[item.FY][item.ExistingOrNew])
              yearTotals[item.FY][item.ExistingOrNew] = 0;
            if (yearData[item.FY]) {
              yearTotals[item.FY][item.ExistingOrNew] += parseFloat(
                item.WorkGeneratedAmount,
              );
              yearData[item.FY]['value'][item.ExistingOrNew][
                'WorkGeneratedAmount'
              ] += parseFloat(item.WorkGeneratedAmount);
            }
          }
        }
      });
    }

    if (period === 'month') {
      fiscalMonths.forEach((month) => {
        newData.push(monthData[month]);
        if (monthTotals[month] && monthTotals[month]['Existing'])
          newTotals.push(monthTotals[month]['Existing']);
        if (monthTotals[month] && monthTotals[month]['New']) {
          newTotals.push(monthTotals[month]['New']);
          max_line = Math.max(max_line, monthTotals[month]['New']);
        }
      });
    } else {
      selectedYears.forEach((year) => {
        newData.push(yearData[year]);
        if (yearTotals[year] && yearTotals[year]['Existing'])
          newTotals.push(yearTotals[year]['Existing']);
        if (yearTotals[year] && yearTotals[year]['New']) {
          newTotals.push(yearTotals[year]['New']);
          max_line = Math.max(max_line, yearTotals[year]['New']);
        }
      });
    }
    
    setData(newData);
    setTotals(newTotals);
    setColors(newColors);
    setMaxLine(max_line);
  }, [queryData, period, selectedYears, defaultStartMonth, defaultMonth]);

  const getColor = useCallback((bar) => {
    const key = bar.key;
    const month = bar.bar.data.month;
    const year = bar.bar.data.year;

    let type = '';
    if (bar['bar']['data']['value'][bar.key])
      type = bar['bar']['data']['value'][bar.key]['type'];

    const activeColor =
      key.indexOf('Existing') > -1 ? negativeActiveColor : '#1c75bc';
    const disableColor =
      key.indexOf('Existing') > -1 ? negativeDisableColor : '#ccdce9';

    if (selectedTopItems.length === 0 && selectedMonths.length === 0) {
      return activeColor;
    }
    if (selectedMonths.indexOf(month) > -1) {
      return activeColor;
    }

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (
        selectedTopItems[i].month === month &&
        selectedTopItems[i].year === year &&
        selectedTopItems[i].type === type
      ) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedMonths, selectedTopItems]);

  const handleBar = useCallback((event, bar) => {
    if (draggedRef.current) return;
    let _selectedBars, _selectedMonths, _selectedTopItems;

    const month = bar.bar.data.month;
    const year = bar.bar.data.year;
    const type = bar['bar']['data']['value'][bar.key]['type'];

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopItems = selectedTopItems.slice();
      _selectedMonths = selectedMonths.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedTopItems.push({ month: month, year: year, type: type });
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedTopItems.splice(index, 1);
      }
    } else {
      _selectedMonths = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedTopItems = [];
        _selectedBars = [];
      } else {
        _selectedTopItems = [{ month: month, year: year, type: type }];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);

    handleFilter({
      selectedTopItems: _selectedTopItems,
      selectedMonths: _selectedMonths,
    });
  }, [selectedBars, selectedMonths, selectedTopItems, handleFilter]);

  const handleLabel = useCallback((event, month) => {
    if (draggedRef.current) return;
    let _selectedMonths, _selectedTopItems, _selectedBars;

    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedMonths = selectedMonths.slice();
      _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);
      } else {
        _selectedMonths.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedTopItems = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      if (!isNaN(index) && selectedMonths.length === 1) {
        _selectedMonths = [];
      } else {
        _selectedMonths = [month];
      }
    }

    setSelectedBars(_selectedBars);

    handleFilter({
      selectedMonths: _selectedMonths,
      selectedTopItems: _selectedTopItems,
    });
  }, [selectedBars, selectedMonths, selectedTopItems, handleFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('wgSelectedTopItems');
    handleFilter({
      selectedMonths: [],
      selectedTopItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {},
      top,
      left;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);

      top = event.nativeEvent.layerY - 20;
      left =
        window.innerWidth / 2 < event.nativeEvent.layerX
          ? event.nativeEvent.layerX - 130
          : event.nativeEvent.layerX + 50;
      data = bar;
    } else {
      data['forecast'] = bar;
    }

    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);

      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  let xTransform = 0;
  let maxYValue = Math.max(
    Math.abs(Math.min(0, Math.min(...totals) * 1.5)),
    Math.max(...totals) * 1.5,
  );
  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (parseInt(thousandFormat(maxYValue)[0]) > 7)
      xTransform = 7.5 * (thousandFormat2(maxYValue).length + 1) + 7;
    else xTransform = 7.5 * thousandFormat2(maxYValue).length + 7;
  }

  const width = window.innerWidth;
  const height = 150;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );
  const offsetX = 3.5;
  
  // scales
  const xScale = scaleBand()
    .domain(period === 'month' ? data.map(x) : data.map(y))
    .rangeRound([0, xMax])
    .padding(0.2);

  const yScale = scaleLinear({
    domain: [
      Math.min(0, Math.min(...totals) * 1.5),
      Math.max(...totals) * 1.5,
    ],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  let fiscalMonths = getMonths(defaultStartMonth);
  let monthKeys = [],
    yearKeys = ['New', 'Existing'];
  selectedYears.forEach((year) => {
    monthKeys.push(year + '_New');
    monthKeys.push(year + '_Existing');
  });

  let line_data = [];
  line_data.push({ x: 0, y: isNaN(yScale(maxLine)) ? 0 : yScale(maxLine) });
  line_data.push({ x: xMax, y: isNaN(yScale(maxLine)) ? 0 : yScale(maxLine) });

  let posBase = [],
    negBase = [];
  data.forEach((i, key) => {
    posBase[key] = isNaN(yScale(0)) ? 0 : yScale(0);
    negBase[key] = isNaN(yScale(0)) ? 0 : yScale(0);
  });

  // Rectangle selection mirrors the month-range gesture used by the People chart.
  // Work Generated selects whole months (both New and Existing components).
  const svgMonthCoordinates = (event) => {
    const bounds = svgRef.current?.getBoundingClientRect();
    if (!bounds) return { x: 0, y: 0 };
    return {
      x: (event.clientX - bounds.left) * (width / bounds.width) - margin.left,
      y: (event.clientY - bounds.top) * (height / bounds.height) - margin.top,
    };
  };

  const onChartMouseDown = (event) => {
    if (period !== 'month' || event.button !== 0 || !svgRef.current) return;
    if (event.target.closest?.('[data-no-drag="true"]')) return;
    const point = svgMonthCoordinates(event);
    dragStartRef.current = { ...point, shift: event.shiftKey };
    draggedRef.current = false;
    setDragRect({ x: point.x, y: point.y, width: 0, height: 0 });
  };

  const onChartMouseMove = (event) => {
    if (!dragStartRef.current || event.buttons !== 1) return;
    const point = svgMonthCoordinates(event);
    const start = dragStartRef.current;
    const x0 = Math.max(0, Math.min(start.x, point.x));
    const x1 = Math.min(xMax, Math.max(start.x, point.x));
    setDragRect({
      x: x0,
      y: Math.max(0, Math.min(start.y, point.y)),
      width: Math.max(0, x1 - x0),
      height: Math.abs(point.y - start.y),
    });
    if (Math.abs(point.x - start.x) > 5 || Math.abs(point.y - start.y) > 5) {
      draggedRef.current = true;
    }
  };

  const onChartMouseUp = (event) => {
    const start = dragStartRef.current;
    if (!start) return;
    dragStartRef.current = null;
    const end = svgMonthCoordinates(event);
    const from = Math.max(0, Math.min(start.x, end.x));
    const to = Math.min(xMax, Math.max(start.x, end.x));
    const dragged = Math.abs(end.x - start.x) > 5 || Math.abs(end.y - start.y) > 5;
    draggedRef.current = dragged;
    setDragRect(null);
    if (!dragged || to <= from) return;
    const monthsInRange = data
      .filter((row) => {
        const left = xScale(row.month);
        return left != null && left < to && left + xScale.bandwidth() > from;
      })
      .map((row) => row.month);
    const months = start.shift
      ? Array.from(new Set([...selectedMonths, ...monthsInRange]))
      : monthsInRange;
    selectedBars.forEach((node) => node?.classList?.remove('barActive'));
    setSelectedBars([]);
    handleFilter({ selectedMonths: months, selectedTopItems: [] });
  };

  const onChartMouseLeave = () => {
    dragStartRef.current = null;
    setDragRect(null);
  };

  const onChartClickCapture = (event) => {
    if (draggedRef.current) {
      event.stopPropagation();
      event.preventDefault();
      draggedRef.current = false;
    }
  };

  return (
    <Grid id="wg_top_chart" container>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        onMouseDown={onChartMouseDown}
        onMouseMove={onChartMouseMove}
        onMouseUp={onChartMouseUp}
        onMouseLeave={onChartMouseLeave}
        onClickCapture={onChartClickCapture}
      >
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="transparent"
          onClick={deSelectAll}
        />

        <Group top={margin.top} left={margin.left}>
          <BarStack
            data={data}
            keys={period === 'month' ? monthKeys : yearKeys}
            x={period === 'month' ? x : y}
            xScale={xScale}
            yScale={yScale}
            color={color}
          >
            {(barStacks) => {
              return barStacks.map((barStack) => {
                return barStack.bars.map((bar) => {                  
                  let y0 = 0,
                    h0 = 0;
                  if (bar.height < 0) {
                    h0 = Math.abs(isNaN(bar.height) ? 0 : bar.height);
                    y0 = negBase[bar.index];
                    negBase[bar.index] -= isNaN(bar.height) ? 0 : bar.height;
                  } else {
                    h0 = isNaN(bar.height) ? 0 : bar.height;
                    posBase[bar.index] -= isNaN(bar.height) ? 0 : bar.height;
                    y0 = posBase[bar.index];
                  }

                  if (!bar.bar?.data[barStack.key]) {
                    y0 = 0;
                    h0 = 0;
                  }

                  return (
                    <rect
                      key={`bar-income-${barStack.index}-${bar.index}`}
                      x={bar.x}
                      y={y0}
                      height={h0}
                      width={
                        bar.width - xTransform / 12 > 0
                          ? bar.width - xTransform / 12
                          : 0
                      }
                      fill={getColor(bar)}
                      onClick={(event) => handleBar(event, bar)}
                      onMouseLeave={(event) => hideTooltipHandler()}
                      onMouseMove={(event) =>
                        showTooltipHandler(event, xScale, bar)
                      }
                      onTouchEnd={(event) => hideTooltipHandler()}
                      onTouchMove={(event) =>
                        showTooltipHandler(event, xScale, bar)
                      }
                    />
                  );
                });
              });
            }}
          </BarStack>

          {dragRect && dragRect.width > 0 && (
            <rect
              x={dragRect.x}
              y={0}
              width={dragRect.width}
              height={yMax}
              fill="#0099cc"
              fillOpacity={0.16}
              stroke="#0077aa"
              strokeWidth={1}
              pointerEvents="none"
            />
          )}

          <LinePath
            data={line_data}
            x={(nx) => nx.x}
            y={(nx) => nx.y}
            stroke="#555555"
            strokeDasharray="6,2"
          />

          <AxisLeft
            numTicks={3}
            scale={yScale}
            stroke="black"
            tickStroke="black"
            tickLabelProps={(value, index) => ({
              fill: 'black',
              fontSize: 11,
              textAnchor: 'end',
              dy: '0.33em',
              dx: '-0.33em',
            })}
            tickComponent={({ formattedValue, ...tickProps }) => (
              <text {...tickProps} fill="black">
                ${formattedValue}
              </text>
            )}
          />

          {fiscalMonths.map((month, index) => {
            return (
              <rect
                key={index}
                x={
                  (xScale.paddingInner() * xScale.step()) / 2 +
                  index * xScale.step() +
                  offsetX
                }
                y={yMax}
                height={margin.bottom}
                fill="transparent"
                onClick={(event) => handleLabel(event, month)}
              />
            );
          })}

          <AxisBottom
            hideTicks={true}
            scale={xScale}
            top={yMax}
            stroke="black"
            tickStroke="black"
            tickLabelProps={(value, index) => ({
              fontSize: 11,
              textAnchor: 'middle',
              dy: '-0.5em',
            })}
            tickComponent={({ formattedValue, ...tickProps }) => (
              <text
                {...tickProps}
                fill={
                  selectedMonths.indexOf(parseInt(formattedValue)) > -1
                    ? activeLabelColor
                    : 'black'
                }
                style={{
                  fontWeight:
                    selectedMonths.indexOf(parseInt(formattedValue)) > -1 ? 700 : 500,
                }}
                onClick={(event) => handleLabel(event, parseInt(formattedValue))}
              >
                {period === 'month'
                  ? enMonths[formattedValue]
                  : formattedValue}
              </text>
            )}
          />
        </Group>
      </svg>

      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop + pageYOffset}
          left={tooltipLeft}
        >
          {tooltipData.isBar ? (
            <div className="bkg-white color-black ft-12">
              <strong>
                {thousandFormat2(
                  tooltipData.bar.data.value[tooltipData.key]
                    ?.WorkGeneratedAmount,
                )}
              </strong>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </Grid>
  );
};

TopChart.propTypes = {
  queryData: PropTypes.object.isRequired,
  period: PropTypes.string.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
